import { prime } from '@src/components/OMS/ManualTradeView/ManualTradeSlice';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { useCustomerTradeCancellation } from '@src/containers/Blotters/CustomerTrades/useCustomerTradeCancellation';
import { CancelDialog } from '@src/containers/Blotters/Trades/CancelDialog';
import { BulkModifyTradeSubAccountDialog } from '@src/containers/Blotters/Trades/ModifyTradeSubAccountDialog';
import type { GenerateOrderDetailsRoute, OrderRouteType } from '@src/containers/Trading/Markets/OrderDetails/types';
import { useEmbeddedContext, useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useNavigateToOrderDetails } from '@src/hooks/useNavigateToOrderDetails';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import {
  ACTION,
  Button,
  filterByCellValueMenuItem,
  FormControlSizes,
  getRowNodesToOperateOn,
  IconName,
  MixpanelEvent,
  TradeStatusEnum,
  useDisclosure,
  useDynamicCallback,
  useJsonModal,
  useMixpanel,
  useSecuritiesContext,
  type AgGridMenuItems,
  type Column,
  type ColumnDef,
  type FilterableProperty,
  type MixpanelInstance,
  type Security,
  type Trade,
  type UseDisclosureReturn,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import { compact } from 'lodash-es';
import { useMemo, useState, type ReactNode } from 'react';
import { openDetailsMenuItem } from '../Orders/useOrderMenu';
import { isCustomerTradeCancellable } from './isCustomerTradeCancellable';
import { getTradeModifyEligibility } from './tradeModifyEligibility';
import { colIDToFilterBuilderKey } from './useTradeFilter';

export function TradeMenu({
  data: trade,
  onShowJson,
}: ICellRendererParams & {
  onShowJson(data: Trade): void;
}) {
  return (
    <>
      <Button size={FormControlSizes.Small} ghost={true} onClick={() => onShowJson(trade)}>
        Show JSON
      </Button>
    </>
  );
}

export const useTradeMenu = ({
  openClause,
  filterableProperties,
  generateOrderDetailsRoute,
  showOpenOrderDetails = true,
  layoutId,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
  generateOrderDetailsRoute: GenerateOrderDetailsRoute;
  showOpenOrderDetails?: boolean;
  layoutId: string | undefined;
}): {
  column: Column;
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<Trade>) => AgGridMenuItems;
  dialogs: ReactNode;
} => {
  const [selectedTrades, setSelectedTrades] = useState<Trade[]>([]);
  const { handleClickJson, jsonModal } = useJsonModal();
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { isAuthorized } = useRoleAuth();
  const modifyManualOrOrphanTradeSubAccountDialog = useDisclosure();
  const cancelManualTradeDialog = useDisclosure();
  const { isEmbedded } = useEmbeddedContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { enableGovernmentBonds, enableShares } = useFeatureFlag();
  const { openCustomerTradeCancellation } = useCustomerTradeCancellation();

  const modifyManualTrade = useDynamicCallback(({ trade }: { trade: Trade }) => {
    dispatch(prime(trade));
    dispatch(openView(OMSView.ManualTradeForm));
  });

  const onOpenDetails = useNavigateToOrderDetails(generateOrderDetailsRoute, layoutId);

  const getCustomContextMenuItems = useDynamicCallback((params: GetContextMenuItemsParams<Trade>) => {
    // Get the selected rows (trades) from the api as of this function running to ensure we get the correct ones
    const trades = compact(getRowNodesToOperateOn(params).map(node => node.data));
    setSelectedTrades(trades);

    const authorizedToEditRegularTrade = isAuthorized(ACTION.EDIT_TRADE);
    const authorizedToCancelCustomerTrade = isAuthorized(ACTION.DEALER_TRADING);

    const rightClickedTrade = params.node?.data;
    if (rightClickedTrade == null) {
      return [];
    }

    return compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      ...modifyCancelManualTradeMenuItem({
        trades,
        modify: modifyManualTrade,
        cancel: () => cancelManualTradeDialog.open(),
        isEmbedded,
        securitiesBySymbol,
        enableGovernmentBonds,
        enableShares,
      }),
      showOpenOrderDetails
        ? openOrderDetailsMenuItem({ trade: rightClickedTrade, onOpenDetails, isEmbedded })
        : undefined,
      modifyManualOrOrphanTradeSubAccountItem({
        trades,
        dialog: modifyManualOrOrphanTradeSubAccountDialog,
        mixpanel,
      }),
      authorizedToEditRegularTrade
        ? modifyRegularTradeMenuItem({
            trades,
            modify: modifyManualTrade,
            isEmbedded,
            securitiesBySymbol,
            enableGovernmentBonds,
            enableShares,
          })
        : undefined,
      authorizedToCancelCustomerTrade
        ? cancelCustomerTradeMenuItem({
            trades,
            cancel: openCustomerTradeCancellation,
            isEmbedded,
          })
        : undefined,
    ]);
  });

  const column = useMemo<Column>(
    () =>
      ({
        type: 'hamburgerMenu',
        id: 'tradeMenu',
        params: {
          renderItems: params => <TradeMenu {...params} onShowJson={handleClickJson} />,
        },
      }) satisfies ColumnDef<Trade>,
    [handleClickJson]
  );

  return {
    column,
    getCustomContextMenuItems,
    dialogs: (
      <>
        {jsonModal}
        <CancelDialog key="cancel-trade-dialog" selectedTrades={selectedTrades} {...cancelManualTradeDialog} />

        <BulkModifyTradeSubAccountDialog
          key="modify-manual-trades-sub-acct"
          selectedTrades={modifyManualOrOrphanTradeSubAccountItem({ trades: selectedTrades, mixpanel })?.trades}
          {...modifyManualOrOrphanTradeSubAccountDialog}
        />
      </>
    ),
  };
};

const openOrderDetailsMenuItem = ({
  trade,
  onOpenDetails,
  isEmbedded,
}: {
  trade: Trade;
  layoutId?: string;
  onOpenDetails: (orderID: string, type: OrderRouteType) => void;
  isEmbedded: boolean;
}) => {
  // Based on the trade, we identify if the orderID on the trade is for a CustomerOrder or a regular Order
  let type: OrderRouteType = 'order';
  // provide a orderID only function to open the details curried with the type
  const curriedOpenDetails = (type: OrderRouteType) => {
    return (orderID: string) => onOpenDetails(orderID, type);
  };

  // 1. presumably a manual trade, no order details available
  if (!trade.OrderID) {
    return null;
  }

  // 2. dealer trade
  if (trade.Market === 'dealer') {
    type = 'customer-order';
  }

  // 3. assume order/:orderID
  return openDetailsMenuItem({
    orders: [{ OrderID: trade.OrderID }],
    onOpenDetails: curriedOpenDetails(type),
    isEmbedded,
  });
};

function buildModifyTradeMenuItem({
  trade,
  modify,
  securitiesBySymbol,
  enableGovernmentBonds,
  enableShares,
}: {
  trade: Trade;
  modify: ({ trade }: { trade: Trade }) => void;
  securitiesBySymbol: Map<string, Security>;
  enableGovernmentBonds: boolean;
  enableShares: boolean;
}) {
  const modifyEligibility = getTradeModifyEligibility(trade, securitiesBySymbol, {
    enableGovernmentBonds,
    enableShares,
  });

  return {
    name: `Modify Trade`,
    action: () => modify({ trade }),
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    disabled: !modifyEligibility.modifiable,
    tooltip: modifyEligibility.reason,
  };
}

export const modifyCancelManualTradeMenuItem = ({
  trades,
  modify,
  cancel,
  isEmbedded,
  securitiesBySymbol,
  enableGovernmentBonds,
  enableShares,
}: {
  trades: Trade[];
  modify: ({ trade }: { trade: Trade }) => any;
  cancel: (tradeID: string) => any;
  isEmbedded: boolean;
  securitiesBySymbol: Map<string, Security>;
  enableGovernmentBonds: boolean;
  enableShares: boolean;
}) => {
  if (!trades || trades.length !== 1 || isEmbedded) {
    return [];
  }
  const trade = trades[0];
  if (!trade.isManualBookedTrade || trade?.TradeStatus === TradeStatusEnum.Canceled) {
    return [];
  }
  return [
    buildModifyTradeMenuItem({ trade, modify, securitiesBySymbol, enableGovernmentBonds, enableShares }),
    {
      name: `Cancel Trade`,
      action: () => cancel(trade.TradeID),
      icon: `<i class="ag-icon ${IconName.MinusCircle}"/>`,
    },
  ];
};

export const cancelCustomerTradeMenuItem = ({
  trades,
  cancel,
  isEmbedded,
}: {
  trades: Trade[];
  cancel: (trade: Trade) => void;
  isEmbedded: boolean;
}) => {
  // Embedded blotters are rendered in constrained environments, so we don't want to show the cancel menu item there.
  if (!trades || trades.length !== 1 || isEmbedded) {
    return undefined;
  }
  const trade = trades[0];
  if (!isCustomerTradeCancellable(trade)) {
    return undefined;
  }

  return {
    name: 'Cancel Trade',
    action: () => cancel(trade),
    icon: `<i class="ag-icon ${IconName.MinusCircle}"/>`,
  };
};

export const modifyRegularTradeMenuItem = ({
  trades,
  modify,
  isEmbedded,
  securitiesBySymbol,
  enableGovernmentBonds,
  enableShares,
}: {
  trades: Trade[];
  modify: ({ trade }: { trade: Trade }) => void;
  isEmbedded: boolean;
  securitiesBySymbol: Map<string, Security>;
  enableGovernmentBonds: boolean;
  enableShares: boolean;
}) => {
  if (!trades || trades.length !== 1) {
    return undefined;
  }
  const trade = trades[0];
  // Don't go this path if we're a manual trade, theres another menu item for that. We're just here for the non-manual trades.
  if (trade.isManualBookedTrade || trade.TradeStatus === TradeStatusEnum.Canceled || isEmbedded) {
    return undefined;
  }

  return buildModifyTradeMenuItem({ trade, modify, securitiesBySymbol, enableGovernmentBonds, enableShares });
};

function modifyManualOrOrphanTradeSubAccountItem({
  trades,
  dialog,
  mixpanel,
}: {
  trades: Trade[];
  dialog?: UseDisclosureReturn;
  mixpanel: MixpanelInstance;
}) {
  const relevantTrades = trades.filter(trade => trade.isManualBookedTrade || trade.isOrphanTrade);
  if (relevantTrades.length === 0) {
    return undefined;
  }

  return {
    name:
      relevantTrades.length === 1
        ? `Modify Sub Account(s)`
        : `Modify Sub Account(s) for ${relevantTrades.length} Trades`,
    action: () => {
      dialog?.open();
      mixpanel.track(MixpanelEvent.ModifySubaccount);
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    trades: relevantTrades,
  };
}
